import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ToastrService } from '@shared/services/toastr.service';
import { Location, NgClass } from '@angular/common';
import { AdminContractService, ContractNumberOrId, ContractTypes } from '@shared/services/admin-contract.service';
import { EditMode } from '@shared/edit.mode';
import _ from 'lodash';
import { HostModeService } from '@shared/services/hostMode.service';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { AdminOrganizationSelectorComponent } from '@shared/components/admin-organization-selector/admin-organization-selector.component';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { ContractOrganizationsComponent } from './contract-organizations/contract-organizations.component';
import { ContractSitesComponent } from './contract-sites/contract-sites.component';
import { ContractClustersComponent } from './contract-clusters/contract-clusters.component';

@Component({
  selector: 'app-add-edit-contract',
  templateUrl: './add-edit-contract.component.html',
  styleUrls: ['./add-edit-contract.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AdminOrganizationSelectorComponent,
    NgSelectComponent,
    NgOptionComponent,
    NgClass,
    ContractOrganizationsComponent,
    ContractSitesComponent,
    ContractClustersComponent,
    TranslatePipe,
  ],
})
export class AddEditContractComponent implements OnInit {
  mode?: EditMode;
  contractRef: ContractNumberOrId;
  organizations: readonly any[];
  contractFormGroup: FormGroup;
  siteModes: Array<{ value: string; label: string }>;
  formGroupInitializedFlag = false;
  protected readonly ContractTypes = ContractTypes;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    protected translate: TranslateService,
    private toastr: ToastrService,
    public adminContractService: AdminContractService,
    public hostModeService: HostModeService,
    public fb: FormBuilder,
  ) {
    this.siteModes = this.hostModeService.modes;
  }
  async ngOnInit() {
    this.initForms();

    const routeParams = this.route.snapshot.paramMap;
    const contractRef = routeParams.get('contractRef');
    if (_.isNil(contractRef)) {
      this.mode = 'create';
      this.contractFormGroup.patchValue({ type: 'BUNDLE' });
    } else {
      this.mode = 'update';
      this.contractRef = contractRef;
      this.contractFormGroup.get('number').setValue(contractRef);
      const result = await this.adminContractService.getContract(this.contractRef);
      this.contractFormGroup.patchValue(result);
    }
  }
  initForms() {
    this.contractFormGroup = this.fb.group({
      name: new FormControl('', Validators.required),
      number: new FormControl({ value: '', disabled: true }),
      holder: new FormControl(null, Validators.required),
      cdnEnabled: new FormControl(false),
      secondaryClusterEnabled: new FormControl(false),
      type: new FormControl(''),
      defaultSiteMode: new FormControl(null),
      startDate: new FormControl(null, [this.dayUnder29Validator]),
      endDate: new FormControl(null),
      createdAt: new FormControl({ value: '', disabled: true }),
      renewalDate: new FormControl(null, [this.dayUnder29Validator]),
      millionRequestsPerMonth: new FormControl(null),
      additionalBandwidth: new FormControl(null),
      nbSitesAdvanced: new FormControl(null),
      nbSitesExpert: new FormControl(null),
      invoicingTimeZone: new FormControl(null),
      monthsaryDay: new FormControl(null),
      bandwidthPerMonth: new FormControl(null),
    });

    this.formGroupInitializedFlag = true;
  }

  createContract() {
    if (!this.contractFormGroup.valid) return;

    this.adminContractService.addContract(this.updateFormatData()).subscribe({
      next: () => this.back(),
      error: () => this.toastr.error(),
    });
  }

  updateContract() {
    if (!this.contractFormGroup.valid) return;

    this.adminContractService.updateContract(this.contractRef, this.updateFormatData()).subscribe({
      next: () => this.back(),
      error: () => this.toastr.error(),
    });
  }

  updateFormatData() {
    const rawValue = _.cloneDeep(this.contractFormGroup.getRawValue());
    const { holder, createdAt, updatedAt, ...rest } = rawValue;

    return {
      ...rest,
      holder: { code: holder.code },
      organizations: [holder.code],
    };
  }
  dayUnder29Validator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const parts = control.value.split('-');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[2], 10);
    if (isNaN(day)) return null;

    return day >= 29 ? { dayTooLate: true } : null;
  }
  back() {
    this.location.back();
  }
}
